import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import { X } from "lucide-react";
import { formatDate, formatINR } from "@/lib/format";

const TH = "px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]";

/** General-ledger drill-down: all postings for one account within the selected period. */
export function LedgerDrilldown({ accountId, range, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/ledger/accounts/${accountId}/postings?range=${range}`)
      .then((r) => setData(r.data))
      .catch((e) => { logError("Drilldown load failed", e); toast.error("Failed to load ledger account"); onClose(); });
  }, [accountId, range, onClose]);

  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-50 flex items-center justify-center p-6 fade-in" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] w-full max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ledger-drilldown">
        <div className="border-b border-[#1A1F16] px-6 py-4 flex items-center justify-between sticky top-0 bg-[#F7F5F0]">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">General Ledger</div>
            <h3 className="font-serif text-2xl tracking-tight" data-testid="drilldown-title">
              {data ? `${data.account.code} · ${data.account.name}` : "Loading…"}
            </h3>
          </div>
          <button onClick={onClose} data-testid="drilldown-close"><X className="w-5 h-5 text-[#6C7565]" /></button>
        </div>

        {data && (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1A1F16] text-left">
                  <th className={TH}>Date</th>
                  <th className={TH}>Particulars</th>
                  <th className={TH}>Source</th>
                  <th className={`${TH} text-right`}>Debit</th>
                  <th className={`${TH} text-right`}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {data.postings.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6C7565]">No postings in this period.</td></tr>
                ) : data.postings.map((p, i) => (
                  <tr key={`${p.date}-${p.source}-${i}`} className="border-b border-[#EAE7DD]" data-testid={`posting-${i}`}>
                    <td className="px-4 py-2 num text-[#6C7565]">{formatDate(p.date)}</td>
                    <td className="px-4 py-2">{p.narration}</td>
                    <td className="px-4 py-2 font-mono text-[9px] uppercase tracking-wider text-[#6C7565]">{p.source}</td>
                    <td className="px-4 py-2 text-right num">{p.debit ? formatINR(p.debit) : "—"}</td>
                    <td className="px-4 py-2 text-right num">{p.credit ? formatINR(p.credit) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-[#1A1F16] font-medium">
                  <td className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest" colSpan={3}>Totals</td>
                  <td className="px-4 py-2.5 text-right num" data-testid="drilldown-total-debit">{formatINR(data.total_debit)}</td>
                  <td className="px-4 py-2.5 text-right num" data-testid="drilldown-total-credit">{formatINR(data.total_credit)}</td>
                </tr>
              </tfoot>
            </table>
            <div className="flex items-center justify-between px-6 py-4 bg-[#1A1F16] text-[#F7F5F0]">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Closing balance</span>
              <span className="font-serif text-2xl num" data-testid="drilldown-closing">
                {formatINR(data.closing.amount)} <span className="text-sm uppercase tracking-widest opacity-70">{data.closing.side}</span>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
